import styled from "styled-components";

import Title from "../../components/title";
import { useTranslation } from "react-i18next";
import {
  CourseDate,
  DropdownOptions,
  Employee,
  ReservedCourse,
  ReservedCourseFilters,
} from "../../types";
import { Color } from "../../constants/color";
import { BreakpointsQuery } from "../../constants/device";
import Course from "../../components/reserved-courses/course";
import React, { useCallback, useEffect, useState } from "react";
import { getAllReserveCourseDateByCompanyId } from "../../features/reservation/utils";
import {
  dateOptionsReserved,
  getLocale,
  initHeadquartersFilterReserved,
  initModesFilter,
  initMonthsFilterByArchiveStatus,
  orderReservationByDate,
} from "../../utils/utils";
import { getEmployeeByCompanyId } from "../../features/employee/utils";
import { contieneTesto } from "../../features/certificate/pipeline";
import CustomSearch from "../../components/search";
import * as Ariakit from "@ariakit/react";
import {
  Select,
  SelectItem,
  SelectLabel,
  SelectProvider,
} from "../../components/form/select";
import { Toggle } from "../../components/form/toggle";
import { capitalize } from "../../utils/utils";
import { Mode } from "../../constants/mode";
import CourseList from "../../components/book-course/vertical-list";
import { ToastContainer } from "react-toastify";
import { GraduationCap } from "lucide-react";
import { StatoVuoto } from "../../components/attestati/stato-vuoto";

const ReservedWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 60px;
  align-items: center;
  justify-content: baseline;
`;

const DropdownSection = styled.div`
  display: flex;
  column-gap: 0.8rem;
  align-items: flex-end;
  svg {
    position: relative;
    top: 1px;
    right: 2px;
  }
  @media ${BreakpointsQuery.tablet} {
    justify-content: center;
    row-gap: 1rem;
  }
`;

const ReservedCourses = () => {
  const { t } = useTranslation();
  const [reservedCourses, setReservedCourses] = useState<ReservedCourse[]>();
  const [reservedCoursesArray, setReservedCoursesArray] = useState<
    ReservedCourse[]
  >(reservedCourses!);
  const [employeeList, setEmployeeList] = useState<Employee[]>();
  const [headquarters, setHeadquarters] = useState<Set<DropdownOptions>>();
  const [modes, setModes] = useState<Set<DropdownOptions>>();
  const [months, setMonths] = useState<Set<DropdownOptions>>();

  const [filters, setFilters] = React.useState<ReservedCourseFilters>({
    courseName: "",
    headquarter: t("book-course-filter-all"),
    month: 0,
    mode: 0,
    archiveStatus: "upcoming",
  });
  const initPage = useCallback(() => {
    getEmployeeByCompanyId()!.then((response) => setEmployeeList(response));
    getAllReserveCourseDateByCompanyId()!.then((response) => {
      const orderedCourses = orderReservationByDate(response);
      setReservedCourses(orderedCourses);
      setReservedCoursesArray(orderedCourses);
      initHeadquartersFilterReserved(response, setHeadquarters, t);
      initMonthsFilterByArchiveStatus(orderedCourses, "upcoming", setMonths, t);
      initModesFilter(setModes, t);
    });
  }, [t]);

  useEffect(() => {
    initPage();
  }, [initPage]);

  const filterCourse = useCallback(() => {
    if (!reservedCourses) return;
    let filtered = reservedCourses;
    if (filters.courseName !== "") {
      filtered = filtered.filter((el) =>
        contieneTesto(Array.from(el.date)[0].courseName, filters.courseName)
      );
    }
    if (filters.headquarter !== t("book-course-filter-all")) {
      const filteredCourses: Map<ReservedCourse, Set<CourseDate>> = new Map();
      filtered.forEach((el) => {
        const filteredDates: Set<CourseDate> = new Set();
        Array.from(el.date).forEach((date) => {
          if (
            date.city.toUpperCase().includes(filters.headquarter.toUpperCase())
          ) {
            filteredDates.add(date);
          }
        });
        filteredCourses.set(el, filteredDates);
      });
      filteredCourses.forEach((dateSet, course) => {
        if (dateSet.size === 0) filteredCourses.delete(course);
      });
      filtered = filtered.filter((el) =>
        Array.from(filteredCourses.keys()).includes(el)
      );
      filtered.forEach((el) => (el.date = filteredCourses.get(el)!));
    }
    if (filters.mode !== 0) {
      const filteredCourses: Map<ReservedCourse, Set<CourseDate>> = new Map();
      filtered.forEach((el) => {
        const filteredDates: Set<CourseDate> = new Set();
        Array.from(el.date).forEach((date) => {
          if (date.mode === filters.mode) {
            filteredDates.add(date);
          }
        });
        filteredCourses.set(el, filteredDates);
      });
      filteredCourses.forEach((dateSet, course) => {
        if (dateSet.size === 0) filteredCourses.delete(course);
      });
      filtered = filtered.filter((el) =>
        Array.from(filteredCourses.keys()).includes(el)
      );
      filtered.forEach((el) => (el.date = filteredCourses.get(el)!));
    }
    if (filters.month !== 0) {
      const filteredCourses: Map<ReservedCourse, Set<CourseDate>> = new Map();
      filtered.forEach((el) => {
        const filteredDates: Set<CourseDate> = new Set();
        Array.from(el.date).forEach((date) => {
          if (new Date(date.date).getMonth() + 1 === filters.month) {
            filteredDates.add(date);
          }
        });
        filteredCourses.set(el, filteredDates);
      });
      filteredCourses.forEach((dateSet, course) => {
        if (dateSet.size === 0) filteredCourses.delete(course);
      });
      filtered = filtered.filter((el) =>
        Array.from(filteredCourses.keys()).includes(el)
      );
      filtered.forEach((el) => (el.date = filteredCourses.get(el)!));
    }
    // Filter by archive status (archived = past months, upcoming = current and future months)
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    const currentYearMonth = currentYear * 100 + currentMonth;
    const filteredCoursesByArchive: Map<
      ReservedCourse,
      Set<CourseDate>
    > = new Map();
    filtered.forEach((el) => {
      const filteredDates: Set<CourseDate> = new Set();
      Array.from(el.date).forEach((date) => {
        const courseDate = new Date(date.date);
        const courseYear = courseDate.getFullYear();
        const courseMonth = courseDate.getMonth() + 1;
        const courseYearMonth = courseYear * 100 + courseMonth;

        if (
          (filters.archiveStatus === "upcoming" &&
            courseYearMonth >= currentYearMonth) ||
          (filters.archiveStatus === "archived" &&
            courseYearMonth < currentYearMonth)
        ) {
          filteredDates.add(date);
        }
      });
      filteredCoursesByArchive.set(el, filteredDates);
    });
    filteredCoursesByArchive.forEach((dateSet, course) => {
      if (dateSet.size === 0) filteredCoursesByArchive.delete(course);
    });
    filtered = filtered.filter((el) =>
      Array.from(filteredCoursesByArchive.keys()).includes(el)
    );
    filtered.forEach((el) => (el.date = filteredCoursesByArchive.get(el)!));
    setReservedCoursesArray(filtered);
  }, [
    reservedCourses,
    filters.courseName,
    filters.headquarter,
    filters.mode,
    filters.month,
    filters.archiveStatus,
    t,
  ]);

  const selectHeadquarter = Ariakit.useSelectStore({
    value: filters.headquarter.toString(),
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, headquarter: value }));
    },
  });

  const selectMode = Ariakit.useSelectStore({
    value: filters.mode.toString(),
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, mode: Number(value) }));
    },
  });
  const selectMonth = Ariakit.useSelectStore({
    value: filters.month.toString(),
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, month: Number(value) }));
    },
  });

  useEffect(() => {
    filterCourse();
  }, [filterCourse, filters]);

  useEffect(() => {}, [reservedCoursesArray]);

  // Update available months when archive status changes
  useEffect(() => {
    if (reservedCourses) {
      initMonthsFilterByArchiveStatus(
        reservedCourses,
        filters.archiveStatus,
        setMonths,
        t
      );
      // Reset month filter when archive status changes
      setFilters((prev) => ({ ...prev, month: 0 }));
    }
  }, [filters.archiveStatus, reservedCourses, t]);

  // Costruisco le card dei corsi visibili (un corso si mostra solo se ha
  // partecipanti tra i dipendenti attuali). Serve anche per capire se, dopo
  // filtri e questo controllo, non resta nulla da mostrare.
  const courseCards = (reservedCoursesArray ?? [])
    .flatMap((singleCourse, index) => {
      const arrayDate = Array.from(singleCourse.date);
      const varMap: Map<number, CourseDate[]> = new Map();
      arrayDate.forEach((el) => {
        varMap.set(el.variationId, Array.of(el));
      });
      const datesSet = Array.from(Array.from(varMap.values())[0]);
      return datesSet.map((singleDate, dateIndex) => {
        let date: Date = new Date(arrayDate[0].date.toString());
        let secondDate: Date | undefined = arrayDate[1]
          ? new Date(arrayDate[1].date.toString())
          : undefined;
        let formattedDate =
          date.toLocaleDateString(getLocale(), dateOptionsReserved) +
          (secondDate
            ? " - " +
              secondDate.toLocaleString(getLocale(), dateOptionsReserved)
            : "");
        let firstHour =
          arrayDate[0].start.toString().slice(0, 5) +
          " - " +
          arrayDate[0].end.toString().slice(0, 5);
        let secondHour = arrayDate[1]
          ? arrayDate[1].start.toString().slice(0, 5) +
            " - " +
            arrayDate[1].end.toString().slice(0, 5)
          : "";
        if (secondDate && date > secondDate) {
          secondDate = new Date(arrayDate[0].date.toString());
          date = new Date(arrayDate[1].date.toString());
          formattedDate =
            date.toLocaleDateString(getLocale(), dateOptionsReserved) +
            (secondDate
              ? " - " +
                secondDate.toLocaleString(getLocale(), dateOptionsReserved)
              : "");
          secondHour =
            arrayDate[0].start.toString().slice(0, 5) +
            " - " +
            arrayDate[0].end.toString().slice(0, 5);
          firstHour = arrayDate[1]
            ? arrayDate[1].start.toString().slice(0, 5) +
              " - " +
              arrayDate[1].end.toString().slice(0, 5)
            : "";
        }
        const participants: Employee[] = employeeList
          ? employeeList.filter((el) =>
              new Set(singleCourse.participantSet).has(el.cf)
            )
          : [];
        return participants.length > 0 ? (
          <Course
            {...singleCourse}
            duration={singleDate.duration ?? 0}
            type={singleCourse.courseType}
            courseName={arrayDate[0].courseName}
            lectureMode={Mode.get(singleDate.mode)?.icon ?? ""}
            courseDate={formattedDate}
            courseHours={firstHour + " " + secondHour}
            city={arrayDate[0].city}
            address={arrayDate[0].address}
            participants={participants}
            variationId={singleCourse.variationId}
            key={index + dateIndex}
          />
        ) : null;
      });
    })
    .filter(Boolean);

  // Se non resta nulla da mostrare, il perche' si decide dall'elenco completo
  // con la stessa regola delle card (un corso conta se ha partecipanti tra i
  // dipendenti attuali), non dai filtri: lo stato parte da Futuri, quindi
  // corsi prenotati tutti passati sono un vuoto da filtri anche senza averne
  // toccato uno.
  const ciSonoCorsiPrenotati = (reservedCourses ?? []).some((corso) => {
    const partecipanti = new Set(corso.participantSet);
    return (employeeList ?? []).some((el) => partecipanti.has(el.cf));
  });

  return (
    <ReservedWrapper>
      {/* Gli avvisi del dettaglio partecipanti hanno bisogno di un contenitore
          montato nella pagina. */}
      <ToastContainer />
      <Title text={t("reserved-courses-title")} />
      <DropdownSection>
        {reservedCoursesArray && (
          <CustomSearch
            name={t("book-course-search-keyword")}
            label={t("book-course-search-placeholder-keyword")}
            labelProps={{
              color: Color.LIGHT_BLUE,
              text: t("book-course-search-keyword"),
              textAlign: "left",
              textTransform: "uppercase",
              fontWeight: "bold",
            }}
            onSearch={(e) => {
              setFilters((prev) => ({ ...prev, courseName: e }));
            }}
          />
        )}
        {headquarters && (
          <SelectProvider store={selectHeadquarter}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.1rem",
              }}
            >
              <SelectLabel
                style={{
                  paddingLeft: "0.5rem",
                  fontSize: "16px",
                  color: Color.LIGHT_BLUE,
                  textTransform: "uppercase",
                  fontFamily: "Avenir Next Bold, sans-serif",
                }}
              >
                {t("book-course-search-headquarter")}
              </SelectLabel>
              <Select
                style={{
                  minWidth: "200px",
                  borderStyle: "solid",
                  borderWidth: "2px",
                  borderRadius: "20px",
                  borderColor: Color.LIGHT_BLUE,
                  padding: "0",
                  paddingLeft: "14px",
                  height: "30px",
                  fontFamily: "Avenir Next Regular, sans-serif",
                }}
                renderValue={(value) =>
                  Array.from(headquarters).find((city) => city.value === value)
                    ?.label
                }
              >
                {Object.entries(Array.from(headquarters)).map(
                  ([key, value]) => (
                    <SelectItem
                      key={`city_${key}`}
                      value={value.label}
                      style={{
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      {value.label}
                    </SelectItem>
                  )
                )}
              </Select>
            </div>
          </SelectProvider>
        )}
        {modes && (
          <SelectProvider store={selectMode}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.1rem",
              }}
            >
              <SelectLabel
                style={{
                  paddingLeft: "0.5rem",
                  fontSize: "16px",
                  color: Color.LIGHT_BLUE,
                  textTransform: "uppercase",
                  fontFamily: "Avenir Next Bold, sans-serif",
                }}
              >
                {t("reserved-courses-search-lecture-mode")}
              </SelectLabel>
              <Select
                style={{
                  minWidth: "200px",
                  borderStyle: "solid",
                  borderWidth: "2px",
                  borderRadius: "20px",
                  borderColor: Color.LIGHT_BLUE,
                  padding: "0",
                  paddingLeft: "14px",
                  height: "30px",
                  fontFamily: "Avenir Next Regular, sans-serif",
                }}
                renderValue={(value) =>
                  t(
                    Array.from(modes).find((cat) => cat.value === value)
                      ?.label ?? ""
                  )
                }
              >
                {Object.entries(Array.from(modes)).map(([key, value]) => (
                  <SelectItem
                    key={`mode_${key}`}
                    value={value.value}
                    style={{
                      justifyContent: "center",
                    }}
                  >
                    {t(value.label)}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </SelectProvider>
        )}
        {months && (
          <SelectProvider store={selectMonth}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.1rem",
              }}
            >
              <SelectLabel
                style={{
                  paddingLeft: "0.5rem",
                  fontSize: "16px",
                  color: Color.LIGHT_BLUE,
                  textTransform: "uppercase",
                  fontFamily: "Avenir Next Bold, sans-serif",
                }}
              >
                {t("book-course-search-month")}
              </SelectLabel>
              <Select
                style={{
                  minWidth: "200px",
                  borderStyle: "solid",
                  borderWidth: "2px",
                  borderRadius: "20px",
                  borderColor: Color.LIGHT_BLUE,
                  padding: "0",
                  paddingLeft: "14px",
                  height: "30px",
                  fontFamily: "Avenir Next Regular, sans-serif",
                }}
                renderValue={(value) => {
                  const month = Array.from(months).find(
                    (m) => m.value === value
                  );
                  return month?.label
                    ? capitalize(month.label)
                    : t("book-course-filter-all");
                }}
              >
                {Object.entries(Array.from(months)).map(([key, value]) => (
                  <SelectItem
                    key={`month_${key}`}
                    value={value.value}
                    style={{
                      justifyContent: "center",
                    }}
                  >
                    {capitalize(value.label)}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </SelectProvider>
        )}
        {months && (
          <Toggle
            label={t("reserved-courses-archive-status")}
            options={[
              {
                value: "archived",
                label: t("reserved-courses-archive-archived"),
              },
              {
                value: "upcoming",
                label: t("reserved-courses-archive-upcoming"),
              },
            ]}
            value={filters.archiveStatus}
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                archiveStatus: value as "archived" | "upcoming",
              }))
            }
          />
        )}
      </DropdownSection>
      {/* Finche' prenotazioni e dipendenti non sono arrivati entrambi non si
          sa quali corsi mostrare: non si mostra ne' l'elenco ne' il vuoto. */}
      {reservedCoursesArray &&
        employeeList &&
        (courseCards.length > 0 ? (
          courseCards
        ) : (
          <StatoVuoto
            icona={GraduationCap}
            titolo={
              ciSonoCorsiPrenotati
                ? t("attestati-tab-corso-vuoto-filtri")
                : t("reserved-courses-empty")
            }
            suggerimento={
              ciSonoCorsiPrenotati
                ? t("reserved-courses-vuoto-filtri-suggerimento")
                : t("reserved-courses-vuoto-suggerimento")
            }
          />
        ))}
    </ReservedWrapper>
  );
};

export default ReservedCourses;
